//@ts-nocheck
/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React, {
  FC,
  JSXElementConstructor,
  ReactElement,
  ReactFragment,
  ReactNode,
} from 'react';
import { Form } from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnDef,
} from '@tanstack/react-table';

export const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, className, ...rest }, ref) => {
    const defaultRef = React.useRef();

    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <Form.Check
        type="checkbox"
        className={classNames('form-check fs-0 mb-0', className)}
      >
        <Form.Check.Input type="checkbox" ref={resolvedRef} {...rest} />
      </Form.Check>
    );
  }
);

// Define the props interface for the AdvanceTableWrapper component
interface AdvanceTableWrapperProps<T> {
  children: ReactNode;
  columns: ColumnDef<object>[];
  data: T;
  sortable: boolean;
  selection: boolean;
  selectionColumnWidth: number;
  pagination: boolean;
  perPage?: number;
}

const AdvanceTableWrapper: FC<AdvanceTableWrapperProps> = ({
  children,
  columns,
  data,
  sortable,
  selection,
  selectionColumnWidth,
  pagination,
  perPage = 10,
}) => {
  // Build the column list, prepending a leading selection column when enabled.
  const resolvedColumns = React.useMemo(() => {
    if (!selection) {
      return columns;
    }
    const selectionColumn = {
      id: 'selection',
      enableSorting: false,
      meta: {
        headerProps: { style: { width: selectionColumnWidth } },
        cellProps: { style: { width: selectionColumnWidth } },
      },
      header: ({ table }) => (
        <IndeterminateCheckbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <div>
          <IndeterminateCheckbox
            checked={row.getIsSelected()}
            indeterminate={row.getIsSomeSelected?.()}
            onChange={row.getToggleSelectedHandler()}
          />
        </div>
      ),
    };
    return [selectionColumn, ...columns];
  }, [columns, selection, selectionColumnWidth]);

  const table = useReactTable({
    data,
    columns: resolvedColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: selection,
    enableSorting: sortable,
    globalFilterFn: 'includesString',
    initialState: {
      pagination: { pageSize: pagination ? perPage : data.length },
    },
  });

  // Adapters that keep the v7 prop names/shape so consuming child components
  // (AdvanceTable, AdvanceTablePagination, AdvanceTableFooter, AdvanceTableSearchBox)
  // do not all need to change at once. AdvanceTable now consumes the raw `table`
  // instance directly; the rest still read these individual adapter props.
  const getTableProps = () => ({});
  const headers = table.getHeaderGroups()[0]?.headers ?? [];
  const page = table.getRowModel().rows;
  const prepareRow = () => {};
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();
  const nextPage = () => table.nextPage();
  const previousPage = () => table.previousPage();
  const gotoPage = (i) => table.setPageIndex(i);
  const setPageSize = (n) => table.setPageSize(n);
  const pageCount = table.getPageCount();
  const { pageIndex, pageSize } = table.getState().pagination;
  const selectedRowIds = table.getState().rowSelection;
  const globalFilter = table.getState().globalFilter;
  const setGlobalFilter = table.setGlobalFilter;

  const recursiveMap = (
    children: ReactNode
  ):
    | (
        | string
        | number
        | ReactElement<any, string | JSXElementConstructor<any>>
        | ReactFragment
      )[]
    | null
    | undefined => {
    return React.Children.map(children, (child) => {
      if (child?.props?.children) {
        return React.cloneElement(child, {
          children: recursiveMap(child.props.children),
        });
      } else {
        if (child?.props?.table) {
          return React.cloneElement(child, {
            ...child.props,
            table,
            getTableProps,
            headers,
            page,
            prepareRow,
            canPreviousPage,
            canNextPage,
            nextPage,
            previousPage,
            gotoPage,
            pageCount,
            pageIndex,
            selectedRowIds,
            pageSize,
            setPageSize,
            globalFilter,
            setGlobalFilter,
          });
        } else {
          return child;
        }
      }
    });
  };

  return <>{recursiveMap(children)}</>;
};

export default AdvanceTableWrapper;
